import React from 'react';
import { Schedule, CheckCircle, Error, RemoveCircleOutline } from '@mui/icons-material';
import { Box, Button, Typography, LinearProgress } from '@mui/material';
import AnimatedProgressIcon from './AnimatedProgressIcon';

// Configuration mapping for different step statuses
export const STATUS_CONFIG = {
    Pending: { 
        text: 'Queued', 
        icon: Schedule, 
        iconClass: 'iconQueued', 
        textClass: 'textQueued' 
    },
    Progress: { 
        text: 'In progress...', 
        icon: AnimatedProgressIcon, 
        iconClass: 'iconInProgress', 
        textClass: 'textInProgress' 
    },
    Completed: { 
        text: 'Completed', 
        icon: CheckCircle, 
        iconClass: 'iconCompleted', 
        textClass: 'textCompleted' 
    },
    Failed: { 
        text: 'Failed', 
        icon: Error, 
        iconClass: 'iconFailed', 
        textClass: 'textFailed' 
    },
    Cancelled: { 
        text: 'Cancelled', 
        icon: RemoveCircleOutline, 
        iconClass: 'iconCancelled', 
        textClass: 'textCancelled' 
    },
};

// Get appropriate title text based on overall saving status
export const getTitle = (overallStatus) => {
    switch (overallStatus) {
        case 'saving': 
            return 'Saving video...';
        case 'completed': 
            return 'Video saved!';
        case 'failed': 
            return 'Saving failed';
        case 'cancelled': 
            return 'Saving cancelled';
        default: 
            return 'Configuration saved!';
    }
};

export const SavingPanelContent = ({ classes, steps, overallStatus, progress, retry }) => {
    // Check if any step has failed to determine progress bar color
    const hasError = steps.some(step => step.status === 'Failed');
    const shouldShowRedProgress = hasError || overallStatus === 'failed';
    
    // Render individual step item
    const renderStepItem = (step, index) => {        
        const config = STATUS_CONFIG[step.status];
        const IconComponent = config.icon;

        return (
            <div 
                key={step.id} 
                className={`${classes.stepItem} ${classes.stepItemAnimated}`}
                style={{
                    animationDelay: `${index * 0.1}s`
                }}
            >
                <div className={classes.stepIcon}>
                    <IconComponent 
                        className={classes[config.iconClass]} 
                        sx={{ fontSize: 20 }} 
                    />
                </div>
                <div className={classes.stepContent}>
                    <div className={classes.stepName}>
                        {step.name}
                    </div>
                    {step.message && (
                        <div className={classes.stepMessage}>
                            {step.message}
                        </div>
                    )}
                </div>
                <div className={`${classes.statusText} ${classes[config.textClass]}`}>
                    {config.text}
                </div>
            </div>
        );
    };
    
    return (
        <Box className={classes.saveWidget}>
            {/* Header with title and progress percentage */}
            <Box className={classes.header}>
                <Typography variant="h6" className={classes.title}>
                    {getTitle(overallStatus)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {Math.round(progress)}%
                </Typography>
            </Box>

            {/* Progress bar */}
            <Box className={classes.progressContainer}>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    className={`${classes.progressBar} ${
                        shouldShowRedProgress ? classes.progressBarRed : classes.progressBarBlue
                    }`}
                />
            </Box>

            {/* Steps list */}
            <div className={classes.stepsList}>
                {steps.map(renderStepItem)}
            </div>

            {hasError && (
                <Box className={classes.buttonContainer}>
                    <Button
                        fullWidth
                        variant="contained"
                        className={`${classes.button} ${classes.retryButton}`}
                        onClick={() => retry()}
                    >
                        Try Again
                    </Button>
                </Box>
            )}
        </Box>
    );
};